import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../utils/api';

const UserInvitePage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'USER',
    companyId: ''
  });
  
  // Fetch companies for superadmins
  useEffect(() => {
    if (currentUser?.role === 'SUPER_ADMIN') {
      const fetchCompanies = async () => {
        try {
          const response = await api.get('/api/companies');
          setCompanies(response.data);
          // Set default companyId if companies are available
          if (response.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              companyId: response.data[0].id
            }));
          }
        } catch (err) {
          console.error('Error fetching companies:', err);
          setError('Failed to load companies.');
        }
      };
      
      fetchCompanies();
    } else if (currentUser?.companyId) {
      // For company admins, use their company ID
      setFormData(prev => ({
        ...prev,
        companyId: currentUser.companyId
      }));
    }
  }, [currentUser]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please complete all required fields');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare user data with admin-provided password
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        isActive: true,
        companyId: formData.companyId // Use selected company ID
      };
      
      // Validate company selection for superadmins
      if (currentUser.role === 'SUPER_ADMIN' && !userData.companyId) {
        setError('As a SuperAdmin, you must specify which company this user belongs to');
        setLoading(false);
        return;
      }
      
      // Add user to the company
      await api.post(`/api/companies/${userData.companyId}/users`, userData);
      
      // Navigate back to users list
      navigate('/app/admin/users');
    } catch (err) {
      console.error('Error inviting user:', err);
      setError(err.response?.data?.message || 'Failed to invite user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Create New User</h1>
          <button
            onClick={() => navigate('/app/admin/users')}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            &larr; Back to Users
          </button>
        </div>
        
        {error && (
          <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h2 className="text-lg font-medium mb-4">User Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="USER">Regular User</option>
                <option value="COMPANY_ADMIN">Company Admin</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Admins can manage users and company settings. Regular users can only access their own inventory.
              </p>
            </div>
            
            {/* Company selector for SuperAdmin */}
            {currentUser?.role === 'SUPER_ADMIN' && (
              <div className="mb-6">
                <label htmlFor="companyId" className="block text-sm font-medium text-gray-700 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <select
                  id="companyId"
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  {companies.length === 0 ? (
                    <option value="">Loading companies...</option>
                  ) : (
                    companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))
                  )}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select the company this user will belong to
                </p>
              </div>
            )}
            
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Users can change their password later in settings if they wish.
              </p>
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-6">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/app/admin/users')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Creating User...' : 'Create User'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserInvitePage;
